import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Alert, Box, Button, Heading, Paragraph, Text, TextButton, Tooltip } from '@zillow/constellation-10';
import { IconExternalOutline } from '@zillow/constellation-icons';

import { logError } from '@zg-rentals/log-error';
import { RentalManagerListingCriteriaType } from '@zg-rentals/ts-rental-manager-api-beta-client';
import { useRentalManagerUserPropertyCriteriaWebApiHandler_get } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { useGetListingDetails } from '../../../../hooks/queries/properties';

import { REVIEW_PAGE_SECTIONS } from '../../../../constants/ListingDetailConstants';
import { NOT_APPLICABLE } from '../../../../constants/ScreeningCriteriaConstants';
import { TYPE_MAP_DISPLAY_VALUE_LC_PET_POLICY } from '../../../../constants/ScreeningCriteriaConstants';

import { FieldEditModal } from '../../../modals/FieldEditModal';

import { wizardHeadingStyles, wizardParagraphStyles } from '../../../../styles/wizard';

import { useAuth } from '../../../AuthProvider';
import IncomeToRentRatioFormField from '../../../PostingPath/IncomeToRentRatioFormField';
import MinCreditScoreFormField from '../../../PostingPath/MinCreditScoreFormField';
import PetPolicyCriteriaFormField, { threeOrMoreMapper } from '../../../PostingPath/PetPolicyCriteriaFormField';
import RecommendationsAlert from '../../../PostingPath/RecommendationsAlert';
import SpinnerContainer from '../../../SpinnerContainer';

import type { ReactNode } from 'react';
import type { UserPropertyCriteriaGetCriteriaWebDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

const DISPLAY_DEFAULT_VALUE = 'Not set';

const animalExemptionURL = 'https://www.hud.gov/program_offices/fair_housing_equal_opp/assistance_animals';

const MODAL_KEY_MAP = {
  LC_INCOME_RATIO: 'LC_INCOME_RATIO',
  LC_CREDIT_RATIO: 'LC_CREDIT_RATIO',
  LC_PET_POLICY: 'LC_PET_POLICY',
} as const;

const MODAL_CONFIG: Record<string, { title: string; heading: string; body: string | ReactNode; formField: ReactNode }> =
  {
    [MODAL_KEY_MAP.LC_INCOME_RATIO]: {
      title: 'Income-to-rent ratio',
      heading: 'What’s your preferred income-to-rent ratio?',
      body: 'This will be displayed on your listing and used to help you review prospective renters, so you can quickly see who’s a good fit for your property.',
      formField: <IncomeToRentRatioFormField />,
    },
    [MODAL_KEY_MAP.LC_CREDIT_RATIO]: {
      title: 'Minimum credit score',
      heading: 'What’s your preferred minimum credit score?',
      body: 'Credit scores fluctuate and don’t show a history of on-time rent payments, so use this with other criteria for a more complete financial picture.',
      formField: <MinCreditScoreFormField />,
    },
    [MODAL_KEY_MAP.LC_PET_POLICY]: {
      title: 'Pet policy',
      heading: 'What’s your pet policy?',
      body: (
        <TextButton asChild icon={<IconExternalOutline />} iconPosition="end" textStyle="body">
          <a target="_blank" href={animalExemptionURL} rel="noopener noreferrer">
            Review the Assistance Animal Exemption
          </a>
        </TextButton>
      ),
      formField: <PetPolicyCriteriaFormField />,
    },
  };

type MODAL_KEY = keyof typeof MODAL_KEY_MAP | '';

// these are used for income and credit score only. pets are handled in a separate function
const getDisplayValueFromCriterion = (
  { criterion, negotiable, type }: Partial<UserPropertyCriteriaGetCriteriaWebDto> = {
    criterion: '',
    negotiable: false,
  },
) => {
  if (!criterion || criterion === NOT_APPLICABLE) {
    return { displayValue: DISPLAY_DEFAULT_VALUE };
  }

  const criterionText =
    type === RentalManagerListingCriteriaType.INCOME_TO_RENT_RATIO ? `${criterion} rent` : `${criterion}`;

  return { displayValue: criterionText, negotiableValue: negotiable ? 'Negotiable' : 'Non-negotiable' };
};

const ScreeningCriteriaContent = ({ onRecsClick }: { onRecsClick: (recKey: string) => void }): JSX.Element => {
  const router = useRouter();
  const { user } = useAuth();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingCriteriaData, isLoading: isListingCriteriaLoading } =
    useRentalManagerUserPropertyCriteriaWebApiHandler_get(
      { listingAlias },
      {
        enabled: Boolean(listingAlias),
        meta: {
          context: {
            api: 'rental-manager',
            details: 'useRentalManagerUserPropertyCriteriaWebApiHandler_get',
          },
          tags: ['users-properties-criteria-get'],
        },
        staleTime: 0,
        retry: 3,
      },
    );

  const { data: listingDetailsData } = useGetListingDetails(listingAlias);

  const criteria = useMemo(() => listingCriteriaData?.criteria || [], [listingCriteriaData]);
  const incomeToRent = useMemo(
    () => criteria.find((criterion) => criterion.type === RentalManagerListingCriteriaType.INCOME_TO_RENT_RATIO),
    [criteria],
  );
  const { displayValue: incomeToRentDisplayValue, negotiableValue: incomeToRentNegotiableValue } = useMemo(
    () => getDisplayValueFromCriterion(incomeToRent),
    [incomeToRent],
  );
  const creditScore = useMemo(
    () => criteria.find((criterion) => criterion.type === RentalManagerListingCriteriaType.CREDIT_SCORE_MIN),
    [criteria],
  );
  const { displayValue: creditScoreDisplayValue, negotiableValue: creditScoreNegotiableValue } = useMemo(
    () => getDisplayValueFromCriterion(creditScore),
    [creditScore],
  );
  const lcPetPolicy = useMemo(() => criteria.filter((criterion) => criterion.type.includes('petPolicy')), [criteria]);

  const isPetPolicySet = useMemo(() => {
    // Use listingDetailsData for consistency with legacy, but fallback to criteria calculation
    const fromListingDetails =
      listingDetailsData?.petsAllowed?.catsDto?.allowed === true ||
      listingDetailsData?.petsAllowed?.smallDogsDto?.allowed === true ||
      listingDetailsData?.petsAllowed?.largeDogsDto?.allowed === true;

    // Fallback: calculate from criteria if listingDetailsData is still loading
    const fromCriteria = lcPetPolicy.some((criterion) => {
      const { type, criterion: value } = criterion;
      if (type === RentalManagerListingCriteriaType.PET_POLICY_DOG_SIZE) return false;
      if (
        type === RentalManagerListingCriteriaType.PET_POLICY_PET_DEPOSIT ||
        type === RentalManagerListingCriteriaType.PET_POLICY_PET_RENT
      ) {
        return false;
      }
      return value && value !== '' && value !== '0';
    });

    return listingDetailsData ? fromListingDetails : fromCriteria;
  }, [lcPetPolicy, listingDetailsData]);

  const { renderArray } = useMemo(() => {
    const renderArray: Array<ReactNode> = [];
    let allButDogSizeEmpty = true;
    let finalNegotiable: null | boolean = null;

    lcPetPolicy.forEach((lcCriterion, index) => {
      const { criterion, type, negotiable } = lcCriterion;

      // skip `dogSize`
      if (type === RentalManagerListingCriteriaType.PET_POLICY_DOG_SIZE) {
        return;
      }

      // skip new types that will replace PET_POLICY_DOG_SIZE petPolicyDogCount
      if (
        type === RentalManagerListingCriteriaType.PET_POLICY_SMALL_DOG_COUNT ||
        type === RentalManagerListingCriteriaType.PET_POLICY_LARGE_DOG_COUNT
      ) {
        return;
      }

      if (!TYPE_MAP_DISPLAY_VALUE_LC_PET_POLICY[type as keyof typeof TYPE_MAP_DISPLAY_VALUE_LC_PET_POLICY]) {
        logError({
          error: new Error(`Missing type: ${type}`),
          errorType: 'ScreeningCriteriaPageError',
          context: 'ScreeningCriteriaPage.lcPetPolicy.forEach',
        });
        return;
      }

      const { label, getValue } =
        TYPE_MAP_DISPLAY_VALUE_LC_PET_POLICY[type as keyof typeof TYPE_MAP_DISPLAY_VALUE_LC_PET_POLICY];

      let displayCriterionValue =
        type === RentalManagerListingCriteriaType.PET_POLICY_CAT_COUNT && isPetPolicySet
          ? getValue(threeOrMoreMapper(criterion) as never, '')
          : DISPLAY_DEFAULT_VALUE;

      if (type === RentalManagerListingCriteriaType.PET_POLICY_DOG_COUNT) {
        // Find the dogSize criterion instead of using hardcoded offset
        const dogSizeCriterion =
          lcPetPolicy.find((c) => c.type === RentalManagerListingCriteriaType.PET_POLICY_DOG_SIZE)?.criterion || '';
        displayCriterionValue = isPetPolicySet
          ? getValue(threeOrMoreMapper(criterion) as never, dogSizeCriterion)
          : DISPLAY_DEFAULT_VALUE;
      }

      // based on logic above this needs to be the last check
      if (
        type === RentalManagerListingCriteriaType.PET_POLICY_PET_DEPOSIT ||
        type === RentalManagerListingCriteriaType.PET_POLICY_PET_RENT
      ) {
        displayCriterionValue =
          criterion && criterion !== '' ? getValue(criterion as never, '') : DISPLAY_DEFAULT_VALUE;
      }

      finalNegotiable = finalNegotiable ?? negotiable;

      if (criterion !== '' || negotiable !== null) {
        allButDogSizeEmpty = false;
      }

      renderArray.push(
        <Paragraph key={`lc-pet-policy-${label}`}>
          {`${label}: `}
          <Text color={displayCriterionValue === DISPLAY_DEFAULT_VALUE ? 'text.subtle' : 'text.neutral'}>
            {displayCriterionValue}
          </Text>
        </Paragraph>,
      );

      if (index + 1 === lcPetPolicy.length) {
        const { label: negotiableLabel, getValue: negotiableGetValue } =
          TYPE_MAP_DISPLAY_VALUE_LC_PET_POLICY['negotiable'];
        const negotiableValue = negotiableGetValue(finalNegotiable, DISPLAY_DEFAULT_VALUE);

        renderArray.push(
          <Paragraph key={`lc-pet-policy-${index + 1}`}>
            {negotiableValue === DISPLAY_DEFAULT_VALUE ? (
              <>
                {`${negotiableLabel}: `}
                <Text color="text.subtle">{negotiableValue}</Text>
              </>
            ) : (
              negotiableValue
            )}
          </Paragraph>,
        );
      }
    });

    return { renderArray, allButDogSizeEmpty };
  }, [lcPetPolicy, isPetPolicySet]);

  const [modalKey, setModalKey] = useState<MODAL_KEY>('');

  const componentRef = useRef({
    initialRender: true,
  });

  const isDisabled = !user?.features?.canUpdateListing;

  useEffect(() => {
    if (!isListingCriteriaLoading) {
      if (componentRef.current.initialRender) {
        componentRef.current.initialRender = false;
        return;
      }
    }
  }, [lcPetPolicy, creditScore, incomeToRent, isListingCriteriaLoading]);

  if (isListingCriteriaLoading) {
    return <SpinnerContainer />;
  }

  return (
    <>
      <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)' }}>
        <Box css={{ gridColumnEnd: 'span 12' }}>
          <RecommendationsAlert sectionName={REVIEW_PAGE_SECTIONS.SCREENING_CRITERIA} onRecsClick={onRecsClick} />
          <Alert css={{ marginBottom: '400' }}>
            These details will appear on your listing within a section called “Landlord’s preferences.” This helps
            potential renters understand whether they’d be a good fit for your property. However, any interested renters
            will still be able to contact you. You can adjust this at any time.
          </Alert>

          {incomeToRent?.isEnabled && (
            <Box data-testid="ListingCriteriaMinimumMonthlyIncomeContainer">
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text textStyle="body-bold">Minimum monthly income</Text>

                <Box css={{ display: 'flex' }}>
                  <Button
                    size="sm"
                    disabled={isDisabled}
                    tone="brand"
                    emphasis="outlined"
                    onClick={() => setModalKey(MODAL_KEY_MAP.LC_INCOME_RATIO)}
                  >
                    Edit
                  </Button>
                </Box>
              </Box>

              <Paragraph
                textStyle={incomeToRentDisplayValue === DISPLAY_DEFAULT_VALUE ? 'body' : 'body-bold'}
                color={incomeToRentDisplayValue === DISPLAY_DEFAULT_VALUE ? 'text.subtle' : 'text.neutral'}
              >
                {incomeToRentDisplayValue}
              </Paragraph>

              {incomeToRentNegotiableValue && <Paragraph>{incomeToRentNegotiableValue}</Paragraph>}
            </Box>
          )}

          {creditScore?.isEnabled && (
            <Box data-testid="ListingCriteriaMinimumCreditScoreContainer">
              <Box css={{ marginBlock: '800' }} />

              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text textStyle="body-bold">Minimum credit score</Text>

                <Box css={{ display: 'flex' }}>
                  <Button
                    size="sm"
                    disabled={isDisabled}
                    tone="brand"
                    emphasis="outlined"
                    onClick={() => setModalKey(MODAL_KEY_MAP.LC_CREDIT_RATIO)}
                  >
                    Edit
                  </Button>
                </Box>
              </Box>

              <Paragraph
                textStyle={creditScoreDisplayValue === DISPLAY_DEFAULT_VALUE ? 'body' : 'body-bold'}
                color={creditScoreDisplayValue === DISPLAY_DEFAULT_VALUE ? 'text.subtle' : 'text.neutral'}
              >
                {creditScoreDisplayValue}
              </Paragraph>

              {creditScoreNegotiableValue && <Paragraph>{creditScoreNegotiableValue}</Paragraph>}
            </Box>
          )}

          <Box data-testid="ListingCriteriaPetPolicyContainer">
            <Box css={{ marginBlock: '800' }} />

            <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text textStyle="body-bold">Pet policy</Text>
              <Box css={{ display: 'flex' }}>
                {listingCriteriaData?.isFeed ? (
                  <Tooltip.Root>
                    <Tooltip.Trigger>
                      <Button
                        size="sm"
                        disabled={true}
                        tone="brand"
                        emphasis="outlined"
                        onClick={() => setModalKey(MODAL_KEY_MAP.LC_PET_POLICY)}
                      >
                        Edit
                      </Button>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content>
                        <Paragraph>To change this information, go to your listing feed.</Paragraph>
                        <Tooltip.Arrow />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                ) : (
                  <Button
                    size="sm"
                    disabled={isDisabled}
                    tone="brand"
                    emphasis="outlined"
                    onClick={() => setModalKey(MODAL_KEY_MAP.LC_PET_POLICY)}
                  >
                    Edit
                  </Button>
                )}
              </Box>
            </Box>

            {renderArray}
          </Box>
        </Box>
      </Box>
      {/* New pattern: Credit score and income ratio use FieldEditModal */}
      {modalKey && (
        <FieldEditModal title={MODAL_CONFIG[modalKey].title} isOpen={true} onClose={() => setModalKey('')}>
          <Heading level={2} textStyle="heading-sm" css={wizardHeadingStyles}>
            {MODAL_CONFIG[modalKey].heading}
          </Heading>

          <Paragraph css={wizardParagraphStyles}>{MODAL_CONFIG[modalKey].body}</Paragraph>

          {MODAL_CONFIG[modalKey].formField}
        </FieldEditModal>
      )}
    </>
  );
};

export default ScreeningCriteriaContent;
